import {
  type ArdoqId,
  type ComponentPath,
  ReferenceDirection,
} from '@ardoq/api-types';
import { isTruthy } from '@ardoq/common-helpers';
import type { HierarchyDefinition } from '@ardoq/data-model';

const getLastLeadingParentChildHopIndex = (
  componentPath: ComponentPath
): number => {
  const firstOtherHopIndex = componentPath.findIndex(
    ({ isParentChildReference }, index) => index > 0 && !isParentChildReference
  );
  return (
    (firstOtherHopIndex === -1 ? componentPath.length : firstOtherHopIndex) - 1
  );
};

const getMovedStartId = (componentPath: ComponentPath): ArdoqId | null => {
  const startIndex = getLastLeadingParentChildHopIndex(componentPath);
  if (startIndex <= 0) {
    return null;
  }
  return componentPath[startIndex].componentId;
};

const startAfterParentChildHops = (
  componentPath: ComponentPath
): ComponentPath => {
  const startIndex = getLastLeadingParentChildHopIndex(componentPath);
  if (startIndex <= 0) {
    return componentPath;
  }
  return [
    {
      componentId: componentPath[startIndex].componentId,
      referenceId: '',
      direction: ReferenceDirection.UNSET,
      isParentChildReference: false,
    },
    ...componentPath.slice(startIndex + 1),
  ];
};

const startHierarchyDefinitionPathsAfterParentChildHops = (
  hierarchyDefinition: HierarchyDefinition
): HierarchyDefinition => {
  const { pathResults, startSetResult } = hierarchyDefinition;
  const movedStartIds = pathResults
    .flat()
    .map(getMovedStartId)
    .filter(isTruthy);
  return {
    ...hierarchyDefinition,
    pathResults: pathResults.map(componentPaths =>
      componentPaths.map(startAfterParentChildHops)
    ),
    startSetResult: [...new Set([...startSetResult, ...movedStartIds])],
  };
};

/**
 * Lets each instance path start at the last component of the parent-child
 * hops it starts with, and adds that component to the start set. A grouping by
 * parent-all rebuilds the removed hops from the start set.
 */
export const startPathsAfterParentChildHops = (
  hierarchyDefinitions: HierarchyDefinition[]
): HierarchyDefinition[] =>
  hierarchyDefinitions.map(startHierarchyDefinitionPathsAfterParentChildHops);
